import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Field, Pick, Section } from "./FormBits";
import { ChargeRows, CollectionPicker } from "./ChargeRows";
import { PreviewTable } from "./PreviewTable";
import { FailureActions } from "./FailureActions";
import { blankForm, buildBody } from "./buildBody";
import { PresetBar } from "./presets";
import { AuDateInput } from "./AuDateInput";
import { ExistingRecordsSection } from "./ExistingRecords";
import { BOND_ACTIONS, COLLECTION_STATUSES, PAY_METHODS, errDetail, money, newKey, settle } from "./settlementApi";

export function RentSection({ f, set, summary }) {
  const increase = f.newRent !== "" && Number(f.newRent) > Number(summary.final_rent);
  return (
    <Section title={`Rent & duration — now ${money(summary.final_rent)} for ${summary.final_days ?? "—"} days`} testid="settle-rent-section">
      <div className="grid grid-cols-2 gap-2">
        <Field label="New rent $"><Input type="number" min="0" step="0.01" value={f.newRent} onChange={(e) => set({ newRent: e.target.value })} className="h-9" data-testid="settle-new-rent" /></Field>
        <Field label="New days"><Input type="number" min="1" value={f.newDays} onChange={(e) => set({ newDays: e.target.value })} className="h-9" data-testid="settle-new-days" /></Field>
      </div>
      {increase && <CollectionPicker mode={f.rentMode} method={f.rentMethod} onMode={(v) => set({ rentMode: v })} onMethod={(v) => set({ rentMethod: v })} testid="settle-rent" />}
      <div className="grid grid-cols-2 gap-2">
        <Field label="Item status"><Pick value={f.collectionStatus} onChange={(v) => set({ collectionStatus: v })} options={COLLECTION_STATUSES} testid="settle-collection-status" /></Field>
        {f.collectionStatus && <Field label="Date (DD/MM/YYYY)"><AuDateInput value={f.collectionDate} onChange={(v) => set({ collectionDate: v })} testid="settle-collection-date" /></Field>}
      </div>
    </Section>
  );
}

export function MoneyBackSection({ f, set, summary }) {
  return (
    <Section title={`Bond (${money(summary.bond_remaining)} left) · refunds · credit`} testid="settle-bond-section">
      <Field label="Bond"><Pick value={f.bondAction} onChange={(v) => set({ bondAction: v })} options={BOND_ACTIONS} testid="settle-bond-action" /></Field>
      {f.bondAction !== "none" && (
        <div className="grid grid-cols-3 gap-2">
          {["partial_refund", "partial_deduction"].includes(f.bondAction) && <Field label="Refund $"><Input type="number" value={f.bondRefund} onChange={(e) => set({ bondRefund: e.target.value })} className="h-9" data-testid="settle-bond-refund" /></Field>}
          {f.bondAction === "partial_deduction" && <Field label="Keep $"><Input type="number" value={f.bondKeep} onChange={(e) => set({ bondKeep: e.target.value })} className="h-9" data-testid="settle-bond-keep" /></Field>}
          {f.bondAction !== "full_deduction" && <Field label="Refund by"><Pick value={f.bondMethod} onChange={(v) => set({ bondMethod: v })} options={PAY_METHODS} testid="settle-bond-method" /></Field>}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <Field label="Refund rent $"><Input type="number" value={f.rentRefund} onChange={(e) => set({ rentRefund: e.target.value })} className="h-9" data-testid="settle-rent-refund" /></Field>
        {Number(f.rentRefund) > 0 && <Field label="Refund by"><Pick value={f.rentRefundMethod} onChange={(v) => set({ rentRefundMethod: v })} options={PAY_METHODS} testid="settle-rent-refund-method" /></Field>}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Mahra Rental Credit $"><Input type="number" value={f.credit} onChange={(e) => set({ credit: e.target.value })} className="h-9" data-testid="settle-credit-amount" /></Field>
        {Number(f.credit) > 0 && <Field label="Expiry months (blank = default)"><Input type="number" min="0" value={f.creditExpiry} onChange={(e) => set({ creditExpiry: e.target.value })} className="h-9" data-testid="settle-credit-expiry" /></Field>}
      </div>
    </Section>
  );
}

export function SettleItemDialog({ open, onClose, booking, summary, onSaved }) {
  const [f, setF] = useState(blankForm);
  const [key] = useState(newKey);
  const [pv, setPv] = useState(null);
  const [failure, setFailure] = useState(null);
  const [sentBody, setSentBody] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (patch) => { setF((x) => ({ ...x, ...patch })); setPv(null); };
  const setCharges = (charges) => set({ charges });

  const doPreview = async () => {
    if (!f.reason.trim()) return toast.error("A reason is required");
    setBusy(true);
    try { setPv(await settle.preview(booking.id, buildBody(f))); } catch (e) { toast.error(errDetail(e).message); } finally { setBusy(false); }
  };
  const doCommit = async () => {
    const body = buildBody(f);
    setBusy(true); setSentBody(body);
    try {
      await settle.commit(booking.id, { ...body, confirm: true, idempotency_key: key });
      toast.success("Settlement saved");
      onSaved?.(); onClose();
    } catch (e) {
      const d = errDetail(e);
      if (d.settlement_state) setFailure(d); else toast.error(d.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#FCFBF7]" data-testid="settle-item-dialog">
        <DialogHeader><DialogTitle className="text-[#282828]">Amend / Settle — {booking.outfit_name}</DialogTitle></DialogHeader>
        {failure ? (
          <FailureActions bookingId={booking.id} settlementKey={key} body={sentBody} detail={failure} onDone={() => { onSaved?.(); onClose(); }} />
        ) : (
          <div className="space-y-4">
            <Field label="Quick presets"><PresetBar f={f} set={set} summary={summary} /></Field>
            <Field label="Reason (kept in the audit trail)"><Textarea value={f.reason} onChange={(e) => set({ reason: e.target.value })} rows={2} data-testid="settle-reason" /></Field>
            <RentSection f={f} set={set} summary={summary} />
            <ExistingRecordsSection f={f} set={set} summary={summary} />
            <Section title="New extra charges" testid="settle-charges-section"><ChargeRows charges={f.charges} setCharges={setCharges} /></Section>
            <MoneyBackSection f={f} set={set} summary={summary} />
            {pv && <PreviewTable pv={pv} />}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose} data-testid="settle-close-btn">Close</Button>
              {!pv ? (
                <Button onClick={doPreview} disabled={busy} className="bg-[#282828] text-white" data-testid="settle-preview-btn">{busy && <Loader2 className="w-3 h-3 animate-spin mr-1" />}Preview</Button>
              ) : (
                <Button onClick={doCommit} disabled={busy} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white" data-testid="settle-confirm-btn">{busy && <Loader2 className="w-3 h-3 animate-spin mr-1" />}Confirm & save settlement</Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
